import React, { useContext } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import Tag from "@/components/common/Tag/Tag";
import { AccountIcon } from "@/components/icons/AccountIcon";
import { ArrowRightNav } from "@/components/icons/ArrowRightNav";
import RggMenNavBanner2 from "@/components/lp/rgg/Nav/RggMenNavBanner2";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementMen } from "@/components/ui-kit/TextElementMen/TextElementMen";
import UserContext from "@/context/UserContext";
import { AccordionNavigationItem } from "@/data/navigation/header.data";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useNavigation } from "@/hooks/useNavigation";
import { PromoData } from "@/types/promoData";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";

import styles from "./AccordionNavigation.module.scss";
import BottomNavigationMobile from "./BottomNavigationMobile";
import MenAccordion from "./MenAccordion";
import MlbMenNavBanner, { mlbBanner } from "./MlbMenNavBanner";
import PromoSaleBannerMen from "./PromoSaleBannerMen";

interface AccordionNavigationProps {
  data: AccordionNavigationItem[];
  toggleNav: (isOpen: boolean) => void;
  navIsOpen: boolean;
  menuIsOpen: boolean;
  promoCf: PromoData;
}

const label = "Shop All Products";
const labeQuiz = "Find Your Routine";

const AccordionNavigation: React.FC<AccordionNavigationProps> = ({
  data,
  toggleNav,
  navIsOpen,
  menuIsOpen,
  promoCf,
}) => {
  const userState = useContext(UserContext);
  const navigate = useNavigation();
  useBodyScrollLock(navIsOpen);

  const trackClicked = (text: string, destination: string | undefined) => {
    const payload = {
      type: "button" as const,
      module_location: "navigation",
      text,
      destination: destination ?? "",
    };

    GA4.onNavigationClicked(payload);
  };

  const handleLinkClick = (text: string = "", destination?: string, donav = false) => {
    setTimeout(() => toggleNav(false), 50);
    trackClicked(text, destination);
    if (destination && destination !== "#" && donav) {
      navigate(destination);
    }
  };

  const handleQuizClick = (altDestination?: string) => {
    const destination = altDestination ?? "/hair/brand/quiz/";
    GA4.onMarketingCtaClicked({
      destination: destination,
      module_location: "navigation",
      text: labeQuiz,
      type: "button",
    });
    setTimeout(() => {
      window.location.href = `${process.env.NEXT_PUBLIC_BASEURL}${destination}`;
    }, 500);
  };

  const generateAccountButton = (isLoggedIn: boolean) => {
    const dest = isLoggedIn ? "/account/" : "/customer/account/login/";
    const label = isLoggedIn ? "My Account" : "Log In";

    return (
      <ButtonNova
        customIcon={isLoggedIn ? <AccountIcon className="w-4" /> : null}
        level="Secondary"
        width="100%"
        label={label}
        icon="Right"
        onClick={() => handleLinkClick(label, dest, true)}
      />
    );
  };

  return (
    <nav className={cn(styles.mobileNavigation, "mobile-navigation")}>
      <div
        className={cn(styles.menuWrapper, "menu-wrapper", {
          [styles.menuIsOpen]: menuIsOpen,
          [styles.navIsOpen]: navIsOpen,
        })}
      >
        {!mlbBanner && promoCf?.savingsData?.web?.nav?.men ? (
          <PromoSaleBannerMen data={promoCf?.savingsData?.web?.nav?.men} toggleNav={toggleNav} />
        ) : null}

        {data.map((section, index) => (
          <div key={section.name}>
            {section.type === "hgn" ? (
              <div
                className={cn(
                  "mx-6 mt-6",
                  !mlbBanner &&
                    "rounded bg-nova-brand-darkblue-60 py-6 md:flex md:h-[414px] md:justify-between md:bg-white md:py-0",
                  !mlbBanner && promoCf && "md:h-auto"
                )}
              >
                {mlbBanner ? (
                  <MlbMenNavBanner
                    className="mb-6 md:mb-8"
                    onClick={() => handleLinkClick("Shop Now", "/hair/men/product/category/")}
                  />
                ) : null}
                <div
                  className={cn(
                    !mlbBanner && "flex-1",
                    mlbBanner && "rounded bg-nova-brand-darkblue-60 py-6 md:bg-white md:py-0"
                  )}
                >
                  <div className="flex-1 md:flex md:flex-col md:rounded md:bg-nova-brand-darkblue-60 md:p-6 md:px-4">
                    <div className={`px-4 pb-2`}>
                      <TextElementMen
                        text={section.title}
                        className="Title-20-Super-Normal uppercase text-nova-base-white"
                      />
                    </div>
                    <ul className={section.name}>
                      {section.children?.map((subitem, idx) => (
                        <li
                          key={idx}
                          className={cn(
                            "group hover:bg-nova-base-solid-20 active:bg-white transition-colors",
                            subitem.className
                          )}
                        >
                          <LinkHandler
                            path={subitem.url}
                            className="pb-4 pt-4  mx-4 block border-b border-nova-transparency-white-20"
                            onClick={() => handleLinkClick(subitem.title, subitem.url)}
                          >
                            <div className={`flex  items-center`}>
                              {subitem.mode === "core" ? (
                                <div className="py-2">
                                  <div className={`flex gap-2 items-center`}>
                                    <TextElementMen
                                      text={subitem.title}
                                      className="text-nova-base-white group-hover:text-nova-base-black  group-active:text-nova-base-black  transition-colors Body-30-Dia-Strong"
                                    />
                                    <div>
                                      <Tag
                                        data={{
                                          line: subitem.subtext,
                                          classes: "bg-nova-brand-accent-80",
                                          fontclasses: "CTA-10-Dia-Mono",
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <TextElementMen
                                    text={subitem.title}
                                    className="text-nova-base-white group-hover:text-nova-base-black  group-active:text-nova-base-black  transition-colors Body-30-Dia-Strong pb-1"
                                  />
                                  <TextElementMen
                                    text={subitem.subtext}
                                    className="text-nova-base-white group-hover:text-nova-base-black group-hover:text-nova-base-active transition-colors Body-10-Dia-Normal"
                                  />
                                </div>
                              )}

                              <div
                                className={`ml-auto invisible group-hover:visible group-active:visible`}
                              >
                                <ArrowRightNav
                                  className={` text-nova-base-white rounded w-6 h-6`}
                                />
                              </div>
                            </div>
                          </LinkHandler>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-6 px-4 md:mt-auto">
                      <ButtonNova
                        icon="Right"
                        width="100%"
                        label={labeQuiz}
                        className="header-quiz-link"
                        onClick={() => handleQuizClick()}
                      />
                    </div>
                  </div>
                </div>
                {!mlbBanner && !promoCf ? (
                  <RggMenNavBanner2 className="mx-4 mt-6 md:ml-8 md:mr-0 md:mt-0" />
                ) : null}
              </div>
            ) : null}

            {section.type === "accordion" ? (
              <MenAccordion
                title={section.title}
                open={section.open ?? false}
                className={section.className ?? ""}
              >
                <div>
                  <ul className={"!pt-0"}>
                    {section.children?.map((subitem, idx) => (
                      <li
                        key={idx}
                        className={
                          "group hover:bg-nova-base-solid-20 active:bg-nova-base-solid-20  transition-colors pb-3 pt-3"
                        }
                      >
                        <LinkHandler
                          className={"px-4 block"}
                          onClick={() => handleLinkClick(subitem.title ?? "", subitem.url)}
                          path={subitem.url ?? "#"}
                          ariaLabel={`Open ${subitem.title ?? ""} page`}
                          id={`SubItemLink-${index}-${idx}`}
                        >
                          <div className={`flex  items-center`}>
                            {subitem.mode === "core" ? (
                              <div>
                                <div className={`flex gap-2 items-center`}>
                                  <TextElementMen
                                    text={subitem.title}
                                    className="text-nova-base-black Body-20-Dia-Strong pb-2"
                                  />
                                  {subitem.subtext ? (
                                    <Tag
                                      data={{
                                        line: subitem.subtext,
                                        classes: "bg-nova-brand-accent-80",
                                        fontclasses: "CTA-10-Dia-Mono",
                                      }}
                                    />
                                  ) : null}
                                </div>
                              </div>
                            ) : subitem.mode === "root-cause" ? (
                              <div>
                                <TextElementMen
                                  text={subitem.title}
                                  classes={{
                                    default: "Body-30-Dia-Normal",
                                    768: "Body-30-Dia-Normal",
                                    1024: "Body-20-Dia-Normal",
                                  }}
                                />
                              </div>
                            ) : (
                              <div>
                                <TextElementMen
                                  text={subitem.longName || subitem.title}
                                  className="text-nova-base-black Body-20-Dia-Strong pb-2"
                                />
                                <TextElementMen
                                  text={subitem.subtext}
                                  className="text-nova-transparency-black-70 Body-10-Dia-Normal pr-6"
                                />
                              </div>
                            )}
                            <div
                              className={`ml-auto invisible group-hover:visible group-active:visible`}
                            >
                              <ArrowRightNav className={`text-nova-base-white rounded w-6 h-6`} />
                            </div>
                          </div>
                        </LinkHandler>
                      </li>
                    ))}
                  </ul>
                </div>
              </MenAccordion>
            ) : null}

            {section.type === "link" ? (
              <button
                className={cn(
                  styles.menuButtonMen,
                  section.title,
                  "border-b border-nova-transparency-black-20"
                )}
                onClick={() => handleLinkClick(section.title, section.url, true)}
                aria-label={section.title}
              >
                <TextElementMen
                  element="span"
                  text={section.title}
                  className="Title-20-Super-Normal uppercase text-nova-base-black"
                />
              </button>
            ) : null}
          </div>
        ))}

        <div className="pt-8 pb-18 px-6">
          <BottomNavigationMobile theme={`men`} />
        </div>
      </div>

      <div className={cn(styles.fixedBottomNav, "h-16", "px-4", "py-2")}>
        <div className="w-1/2 pr-1">{generateAccountButton(userState.isLoggedIn)}</div>
        <div className={cn(styles.fixedBottomNavProducts, "w-1/2 pl-1")}>
          <ButtonNova
            level="Secondary"
            icon="Right"
            width="100%"
            label={label}
            onClick={() => handleLinkClick(label, "/hair/men/product/category/", true)}
          />
        </div>
      </div>
    </nav>
  );
};

export default AccordionNavigation;
